import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import cn from 'classnames';
import { css, StyleSheet } from 'aphrodite';
import { ChevronRight } from 'lucide-react';
import { useThemeContext } from '../../../utils/providers/theme-context';

export type BreadcrumbItem = {
  label: string;
  path?: string;
};

type BreadcrumbProps = {
  items: BreadcrumbItem[];
  className?: string;
};

/**
 * Breadcrumb - Navigation breadcrumb component
 * - Shows navigation path with clickable links
 * - Last item is not clickable (current page)
 * - Themed with proper colors
 */
export default function Breadcrumb({ items, className }: BreadcrumbProps) {
  const navigate = useNavigate();
  const { getColour } = useThemeContext();

  const styles = useMemo(
    () =>
      StyleSheet.create({
        container: {
          padding: '4px 8px',
          marginBottom: '20px'
        },
        link: {
          color: getColour('activeText'),
          ':hover': {
            textDecoration: 'underline'
          }
        },
        current: {
          color: getColour('normalText')
        },
        separator: {
          color: getColour('inactiveText')
        }
      }),
    [getColour]
  );

  const handleClick = (path?: string) => {
    if (path) {
      navigate(path);
    }
  };

  return (
    <nav
      className={cn(
        'flex items-center gap-2 px-4 sm:px-0',
        className,
        css(styles.container)
      )}
    >
      {items.map((item, index) => {
        const isLast = index === items.length - 1;
        const isClickable = !isLast && item.path;

        return (
          <div key={index} className="flex items-center gap-2">
            {index > 0 && (
              <ChevronRight size={16} className={css(styles.separator)} />
            )}
            {isClickable ? (
              <button
                onClick={() => handleClick(item.path)}
                className={cn(
                  'text-sm font-medium hover:underline cursor-pointer',
                  css(styles.link)
                )}
              >
                {item.label}
              </button>
            ) : (
              <span className={cn('text-sm font-medium', css(styles.current))}>
                {item.label}
              </span>
            )}
          </div>
        );
      })}
    </nav>
  );
}
